import {Type, Location} from "utils/types";
import React, {FC} from "react";
import TopBarRedesigned from "components/TopBarRedesigned/TopBarRedesigned";
import {ShiftDataProps} from "../PostJobPage/PostJob";
import {
    Page,
    HeaderDescription,
    LocationContainer,
    Title,
    Description,
    ShiftContainer,
    ShiftBoxRateTitle,
    ShiftBoxRateValue,
    ShiftBoxRate,
    InfoBox,
    ShiftBox,
    Info,
    ShiftButton,
    ButtonBox,
    ShiftIsReadyCancelPolicy,
    Link
} from "./PostJobConfirmation.styles";
import dayjs from "dayjs";
import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import {CreditCard} from "@mui/icons-material";

interface ShiftIsReadyProps {
    shift: ShiftDataProps,
    role: Type,
    location: Location,
    from: string,
    to: string,
    onEditShift: () => void,
    onPostShift: () => void
    updatePaymentInfo?: (e: any) => void
}

const PostJobConfirmation: FC<ShiftIsReadyProps> = (
    {
        shift,
        role,
        location,
        from,
        to,
        onEditShift,
        onPostShift
    }
) => {
    const roleString = role === Type.Hygienist ? 'Dental Hygienist' : 'Dental Assistant'
    let paymentTerm: string
    let paysW2: string

    switch (shift.paymentTerms) {
        case 1:
            paymentTerm = 'within 24 hours of'
            break;
        case 14:
            paymentTerm = 'within two weeks of'
            break;
        case 30:
            paymentTerm = 'within one month of'
            break;
        default:
            paymentTerm = 'within 24 hours of'
    }

    switch (shift.paysW2) {
        case 0:
            paysW2 = '1099 Independent Contractor'
            break;
        case 1:
            paysW2 = 'W2 Temporary Employee'
            break;
        case 2:
            paysW2 = 'as either 1099 or W2'
            break;
        default:
            paysW2 = 'as either 1099 or W2'
    }

    const title = `Review Your Shift Details for ${dayjs(shift.startDate).format('dddd, MMM D')}`

    return (
        <>
            <TopBarRedesigned
                onBack={onEditShift}
                title={title}
            />
            <Page>
                <HeaderDescription>
                    Role: {roleString}
                </HeaderDescription>

                <LocationContainer>
                    <Title>
                        {location.practiceName}
                    </Title>
                    <Description>
                        {location.address}, {location.province} {location.postalCode}
                    </Description>
                </LocationContainer>

                <ShiftContainer>
                    <ShiftBox>
                        <Title>
                            Shift Hours
                        </Title>
                        <Description>
                            Start at: {dayjs(from).format('h:mma')}
                        </Description>
                        <Description>
                            Shift ends: {dayjs(to).format('h:mma')}
                        </Description>
                        <InfoBox>
                            {`${dayjs(from).format('h:mma')} is the time you want your TempStars ${roleString} `
                                + `to arrive at your office, changed into scrubs and ready to work. `
                                + `There is no expectation for unpaid early arrival for prep/orientation, `
                                + `so ensure that you accommodate that in your Start Time if needed.`}
                        </InfoBox>
                    </ShiftBox>
                    <ShiftBoxRate>
                        <ShiftBoxRateTitle>Proposed Rate</ShiftBoxRateTitle>
                        <ShiftBoxRateValue>${shift.proposedRate}/hr</ShiftBoxRateValue>
                    </ShiftBoxRate>
                </ShiftContainer>

                {shift.instaBook ? <Info>
                    <CheckCircleOutlined />
                    <div>
                        <Description>
                            <b>Instabook Enabled</b>
                        </Description>
                        <InfoBox>
                            Allows your {shift.country ? 'Favorited' : 'Favourited'} Dental Professionals as well as Pro & Elite Status users to instantly book this shift at your proposed rate without review/approval. You can still review custom offers.
                        </InfoBox>
                    </div>
                </Info> : null}

                <Info>
                    <CreditCard />
                    <div>
                        <Description>
                            <b>Payment Terms</b>
                        </Description>
                        <InfoBox>
                            {`You agree to pay your ${roleString} ${paymentTerm} the shift. `}
                            <Link
                                href={'https://www.tempstars.com/direct_payment'}
                                target="_system"
                                className={"external"}
                            >TempStars Late Payment Policy</Link>
                        </InfoBox>
                    </div>
                </Info>

                {shift.country ? <Info>
                    <CreditCard />
                    <div>
                        <Description>
                            <b>Working Relationship Terms</b>
                        </Description>
                        <InfoBox>
                            {`You agree to pay your ${roleString} as a ${paysW2} for this shift.`}
                        </InfoBox>
                    </div>
                </Info> : null}

                <ButtonBox>
                    <ShiftButton
                        variant="contained"
                        color="primary"
                        size="large"
                        id="continue-button"
                        onClick={onPostShift}
                    >
                        Post This Shift
                    </ShiftButton>
                    <ShiftButton
                        variant="outlined"
                        color="primary"
                        size="large"
                        id="edit-button"
                        onClick={onEditShift}
                    >
                        Edit Shift Details
                    </ShiftButton>
                </ButtonBox>
                <ShiftIsReadyCancelPolicy>
                    By posting this shift you are agreeing to <a
                    href={'https://www.tempstars.com/short-notice'}
                    target="_system" className={"external"}
                >TempStars Cancellation Policy</a>
                </ShiftIsReadyCancelPolicy>
            </Page>
        </>
    )
}

export default PostJobConfirmation;